"use client"

import { useState, useEffect, useCallback } from "react"
import type { User } from "firebase/auth"
import { doc } from "firebase/firestore"
import { auth, db, loggedSetDoc, loggedGetDoc } from "../lib/firebase"

export function useEmailVerification(user: User | null) {
  const [isVerified, setIsVerified] = useState(false)

  const updateFirestore = useCallback(async (uid: string) => {
    if (!uid) return

    try {
      const userDocRef = doc(db, "users", uid)
      const userData = await loggedGetDoc(userDocRef)

      if (!userData) return

      // Only update Firestore if emailVerified is false or missing
      if (userData.emailVerified !== true) {
        await loggedSetDoc(userDocRef, { emailVerified: true }, { merge: true })
      }
    } catch (error) {
      console.error("Error updating Firestore:", error)
    }
  }, [])

  useEffect(() => {
    let unsubscribe = () => {}
    let intervalId: NodeJS.Timeout | null = null

    const checkVerification = async (currentUser: User | null) => {
      if (currentUser) {
        try {
          await currentUser.reload()
          const isEmailVerified = currentUser.emailVerified
          setIsVerified(isEmailVerified)

          if (isEmailVerified) {
            await updateFirestore(currentUser.uid)
            if (intervalId) {
              clearInterval(intervalId)
              intervalId = null
            }
          }
        } catch (error) {
          console.error("Error checking email verification:", error)
        }
      } else {
        setIsVerified(false)
        if (intervalId) {
          clearInterval(intervalId)
          intervalId = null
        }
      }
    }

    unsubscribe = auth.onAuthStateChanged((currentUser) => {
      // Only set up verification checking if the user exists and is not verified
      if (currentUser) {
        if (currentUser.emailVerified) {
          // If already verified, just set state and update Firestore once
          setIsVerified(true)
          updateFirestore(currentUser.uid)
        } else {
          // Only check periodically if not verified
          checkVerification(currentUser)
          intervalId = setInterval(() => checkVerification(currentUser), 10000) // Check every 10 seconds
        }
      } else {
        setIsVerified(false)
      }
    })

    return () => {
      unsubscribe()
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [updateFirestore])

  return isVerified
}

